import React from "react";
import { Bell, BellOff } from "lucide-react";

/**
 * ReminderEditor — multi-pick chip group for selecting one or more reminder
 * offsets (in minutes) before an event/chore/dose. Mirrors Google Calendar's
 * UX: chips toggle, "None" clears, "+" lets you add an arbitrary value.
 *
 * `value` is `number[]` (minutes before). `[0]` = "at the time of",
 * `[]` = no reminders.
 */
const PRESETS = [
  { v: 0,     label: "At time" },
  { v: 5,     label: "5 min"   },
  { v: 10,    label: "10 min"  },
  { v: 15,    label: "15 min"  },
  { v: 30,    label: "30 min"  },
  { v: 60,    label: "1 hr"    },
  { v: 120,   label: "2 hr"    },
  { v: 1440,  label: "1 day"   },
  { v: 2880,  label: "2 days"  },
];

export function ReminderEditor({ value = [60], onChange, testidPrefix = "reminder" }) {
  const selected = new Set((value || []).map((v) => Number(v)));

  const toggle = (v) => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange?.(Array.from(next).sort((a, b) => a - b));
  };

  const clear = () => onChange?.([]);

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center gap-2">
        {selected.size === 0 ? (
          <BellOff className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <Bell className="w-3.5 h-3.5 text-primary" />
        )}
        <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
          Reminders
        </span>
        {selected.size > 0 && (
          <button
            type="button"
            data-testid={`${testidPrefix}-clear`}
            onClick={clear}
            className="ml-auto text-[11px] text-muted-foreground tap"
          >
            None
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => {
          const on = selected.has(p.v);
          return (
            <button
              key={p.v}
              type="button"
              data-testid={`${testidPrefix}-chip-${p.v}`}
              onClick={() => toggle(p.v)}
              aria-pressed={on}
              className={[
                "h-8 px-3 rounded-full text-[12px] font-medium tap border transition-colors",
                on
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-card text-foreground border-border",
              ].join(" ")}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {selected.size > 0 && (
        <p className="text-[11px] text-muted-foreground">
          You'll get {selected.size} notification{selected.size > 1 ? "s" : ""} ahead of this.
        </p>
      )}
    </div>
  );
}
